import { CloudActions } from '@portal/portal-core';
import React from 'react';

import { CloudAdminActions } from 'actions';
import { SuperAdminActions as SuperAdminDuckActions } from 'ducks/super-client';
import { seatFeaturesKey } from 'ducks/super-client/reducers/seat-features';
import {
  currentProductAccessSelector,
  currentTabSelector,
  getAdminInvites,
  regionSelector,
  selectActiveUserRows,
  selectedRowsSelector,
} from 'selectors';
import type { AppDispatch } from 'store';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import ManageUsersModal from './manage-users-modal';

const clearSeatEditingConfirmation = (dispatch: AppDispatch): void => {
  dispatch(CloudActions.clearProductSetUsersEndDateResult());
  dispatch(CloudActions.clearSetUsersSuspendedResult());
  dispatch(CloudActions.clearProductSetUsersArchivedDateResult());
};

interface ProductSetUsersEndDateArgs {
  productName: string;
  selectedIdsForDateMod: string[];
  costCenterId: string;
  productAccessId: string;
  endDate: string;
}

interface ProductSetUsersArchivedDateArgs {
  productName: string;
  userIds: string[];
  costCenterId: string;
  productAccessId: string;
  archivedDate: string;
}

interface ManageProductUsersArgs {
  productName: string;
  rowsSelectedIdsArray: string[];
  costCenterId: string;
  productAccessId: string;
  suspended: boolean;
}

const ManageUsers = (): React.ReactElement | null => {
  const dispatch = useAppDispatch();

  const cloudAdminState = useAppSelector((state) => state.cloudAdmin);
  const userAdminState = useAppSelector((state) => state.userAdmin);
  const adminInvites = useAppSelector(getAdminInvites);
  const selectedRows = useAppSelector(selectActiveUserRows);
  // Used for post-operation messaging, where we DON'T want to account for current row visibility:
  const selectedRowsAll = useAppSelector((state) => selectedRowsSelector(state).selectedRows);
  const currentProductAccess = useAppSelector(currentProductAccessSelector);
  const selectedProduct = useAppSelector(currentTabSelector);
  const selectedRegion = useAppSelector(regionSelector);
  // Per-seat feature overrides for the product access on screen. Empty for a non-superadmin:
  // the fetch is never issued, and the section that consumes this does not render.
  const seatFeatures = useAppSelector((state) => {
    const costCenterId = state.userAdmin?.costCenter?.id;
    const productAccessId = (currentProductAccess as { id?: string } | undefined)?.id;
    if (!costCenterId || !productAccessId) {
      return undefined;
    }
    return state.superAdmin?.superClient?.seatFeatures?.[
      seatFeaturesKey(costCenterId, productAccessId)
    ];
  });

  const { costCenter } = userAdminState;
  const {
    modals: { feature, showModal },
  } = cloudAdminState;

  if (!(feature === 'manage-users' && showModal && costCenter)) {
    return null;
  }

  const modalProps = {
    cloudAdminState,
    userAdminState,
    adminInvites,
    selectedRows,
    selectedRowsAll,
    currentProductAccess,
    selectedProduct,
    selectedRegion,
    seatFeatures,
    fetchSeatFeatures: (costCenterId: string, productAccessId: string) =>
      dispatch(SuperAdminDuckActions.fetchSeatFeatures(costCenterId, productAccessId)),
    setSeatFeature: (args: {
      costCenterId: string;
      productAccessId: string;
      userIds: string[];
      featureName: string;
      value: boolean | string | number;
      enabled: boolean | null;
    }) => dispatch(SuperAdminDuckActions.setSeatFeature(args)),
    hideModal: () => dispatch(CloudAdminActions.hideModal()),
    clearRowsSelected: () => dispatch(CloudAdminActions.clearRowsSelected()),
    closeProductManageUsersResults: () => clearSeatEditingConfirmation(dispatch),
    closeSetUsersSuspendedResults: () => clearSeatEditingConfirmation(dispatch),
    closeProductSetUsersArchivedDateResults: () => clearSeatEditingConfirmation(dispatch),
    productSetUsersEndDate: ({
      productName,
      selectedIdsForDateMod,
      costCenterId,
      productAccessId,
      endDate,
    }: ProductSetUsersEndDateArgs) =>
      dispatch(
        CloudActions.productSetUsersEndDate(
          productName,
          selectedIdsForDateMod,
          costCenterId,
          productAccessId,
          endDate,
        ),
      ),
    productSetUsersArchivedDate: ({
      productName,
      userIds,
      costCenterId,
      productAccessId,
      archivedDate,
    }: ProductSetUsersArchivedDateArgs) =>
      dispatch(
        CloudActions.productSetUsersArchivedDate(
          productName,
          userIds,
          costCenterId,
          productAccessId,
          archivedDate,
        ),
      ),
    manageProductUsers: ({
      productName,
      rowsSelectedIdsArray,
      costCenterId,
      productAccessId,
      suspended,
    }: ManageProductUsersArgs) =>
      dispatch(
        CloudActions.productSetUsersSuspended(
          productName,
          rowsSelectedIdsArray,
          costCenterId,
          productAccessId,
          suspended,
        ),
      ),
  };

  return <ManageUsersModal {...modalProps} />;
};

export default ManageUsers;
